import React, { useState, useEffect } from "react";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
  defaultDropAnimationSideEffects,
  DropAnimation,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { Box } from "@mui/material";
import { KanbanColumn } from "./KanbanColumn";
import { KanbanCard } from "./KanbanCard";

interface KanbanBoardProps {
  tasks: any[];
  onStatusChange: (taskId: string, newStatus: string) => void;
  onEdit: (taskId: string) => void;
}

const columns = [
  { id: "not_started", title: "Not Started" },
  { id: "in_progress", title: "In Progress" },
  { id: "on_hold", title: "On Hold" },
  { id: "completed", title: "Completed" },
];

const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: { opacity: "0.5" },
    },
  }),
};

export const KanbanBoard: React.FC<KanbanBoardProps> = ({
  tasks,
  onStatusChange,
  onEdit,
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [items, setItems] = useState<any[]>(tasks);

  // Jira-style column freezing
  const [draggingHeight, setDraggingHeight] = useState<number | null>(null);
  const [draggingStatus, setDraggingStatus] = useState<string | null>(null);

  useEffect(() => setItems(tasks), [tasks]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const findContainer = (id: string) => {
    const task = items.find((item) => item.task_id === id);
    if (task) return task.status;
    if (columns.find((c) => c.id === id)) return id;
    return undefined;
  };

  const handleDragStart = (event: DragStartEvent) => {
    const id = event.active.id as string;
    setActiveId(id);

    const t = items.find((i) => i.task_id === id);
    setDraggingStatus(t?.status ?? null);

    // capture card DOM height for placeholder
    const el = document.querySelector(`[data-task-id="${id}"]`) as HTMLElement;
    if (el) setDraggingHeight(el.clientHeight);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    const activeContainer = findContainer(activeId);
    const overContainer = findContainer(overId);

    if (!activeContainer || !overContainer) return;

    // same column = reorder
    if (activeContainer === overContainer) {
      setItems((prev) => {
        const colTasks = prev.filter((t) => t.status === activeContainer);
        const otherTasks = prev.filter((t) => t.status !== activeContainer);

        const oldIndex = colTasks.findIndex((t) => t.task_id === activeId);
        let newIndex = colTasks.findIndex((t) => t.task_id === overId);
        if (newIndex === -1) newIndex = colTasks.length - 1;

        const reordered = arrayMove(colTasks, oldIndex, newIndex);
        return [...otherTasks, ...reordered];
      });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);
    setDraggingHeight(null);
    setDraggingStatus(null);

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    const activeContainer = findContainer(activeId);
    const overContainer = findContainer(overId);

    if (!activeContainer || !overContainer) return;

    if (activeContainer === overContainer) return;

    // update status
    onStatusChange(activeId, overContainer);

    setItems((prev) =>
      prev.map((item) =>
        item.task_id === activeId
          ? { ...item, status: overContainer }
          : item
      )
    );
  };

  const activeTask = activeId
    ? items.find((t) => t.task_id === activeId)
    : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
          height: "100%",
          overflowX: "auto",
          width: "100%",
        }}
      >
        {columns.map((col) => {
          const isFrozen = draggingStatus === col.id && activeId !== null && draggingHeight;
          return (
            <Box key={col.id} sx={{ flex: 1, minWidth: 280 }}>
              <KanbanColumn
                id={col.id}
                title={col.title}
                tasks={items.filter((t) => t.status === col.id)}
                onEdit={onEdit}
              />
              {isFrozen && (
                <Box
                  sx={{
                    height: draggingHeight,
                    borderRadius: 1,
                    mt: 1,
                    opacity: 1,
                    border: "2px dashed #999",
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
      <DragOverlay dropAnimation={dropAnimation}>
        {activeTask ? (
          <KanbanCard
            id={activeTask.task_id}
            task={activeTask}
            onClick={() => { }}
            isOverlay
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
